<template>
  <div class="sparkWrap" :title="tooltip">
    <svg v-if="hasData" :width="width" :height="height" class="sparkSvg">
      <!-- Engaged line (top, green) -->
      <polyline
        v-if="engagedPath"
        :points="engagedPath"
        class="sparkLineEngaged"
      />
      <!-- Outbound line (bottom, gray) -->
      <polyline
        v-if="outboundPath"
        :points="outboundPath"
        class="sparkLineOutbound"
      />
    </svg>
    <span v-else class="noData">—</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type ActivityTimeline = {
  engaged: number[]
  outbound: number[]
}

const props = withDefaults(defineProps<{
  timeline: ActivityTimeline
  width?: number
  height?: number
}>(), {
  width: 120,
  height: 32,
})

const PADDING_Y = 2

const hasData = computed(() => {
  const t = props.timeline
  return (t.engaged?.length > 0 && t.engaged.some(v => v > 0))
    || (t.outbound?.length > 0 && t.outbound.some(v => v > 0))
})

function buildPath(data: number[], yOffset: number, laneHeight: number): string | null {
  if (!data?.length || data.every(v => v === 0)) return null
  const padded = [0, ...data, 0]
  const max = Math.max(...padded, 1)
  const stepX = props.width / Math.max(padded.length - 1, 1)
  return padded.map((v, i) => {
    const x = Math.round(i * stepX * 10) / 10
    const y = Math.round((yOffset + laneHeight - PADDING_Y - (v / max) * (laneHeight - PADDING_Y * 2)) * 10) / 10
    return `${x},${y}`
  }).join(' ')
}

const laneHeight = computed(() => props.height / 2)

const engagedPath = computed(() =>
  buildPath(props.timeline.engaged, 0, laneHeight.value)
)

const outboundPath = computed(() =>
  buildPath(props.timeline.outbound, laneHeight.value, laneHeight.value)
)

const tooltip = computed(() => {
  const t = props.timeline
  const engTotal = t.engaged?.reduce((a, b) => a + b, 0) || 0
  const outTotal = t.outbound?.reduce((a, b) => a + b, 0) || 0
  const parts: string[] = []
  if (engTotal) parts.push(`${engTotal} engaged (replies, calls, meetings)`)
  if (outTotal) parts.push(`${outTotal} outbound (reachouts sent)`)
  return parts.length ? `60-day timeline: ${parts.join(', ')}` : 'No activity in last 60 days'
})
</script>

<style scoped>
.sparkWrap {
  display: flex;
  align-items: center;
  height: 32px;
}

.sparkSvg {
  display: block;
}

.sparkLineEngaged {
  fill: none;
  stroke: var(--color-accent, #059669);
  stroke-width: 1.5;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.sparkLineOutbound {
  fill: none;
  stroke: var(--color-text-tertiary, #94a3b8);
  stroke-width: 1.5;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.noData {
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
}
</style>
